import { useEffect, useRef } from "react";
import ActionCard from "./ActionCard";
import type { ActionCard as ActionCardType } from "../types/cards";

interface Props {
  cards: ActionCardType[];
  loading: boolean;
  // Soft note shown when the model attempted a card that failed
  // validation server-side.
  cardError: boolean;
  // True between the card_pending SSE event (sentinel reached) and the
  // real card arriving — renders a skeleton so the reply's "card's
  // ready" never points at an empty column.
  preparing: boolean;
  // Card to scroll into view + highlight (dashboard deep link).
  focusCardId: string | null;
  onApply: (cardId: string, lineIds: string[]) => Promise<unknown>;
  onRetry: (cardId: string, lineId: string) => Promise<unknown>;
  onDismiss: (cardId: string) => Promise<unknown>;
}

// Right-hand column of the Ask panel (stacks below the thread on
// phones): pending plans first, then applied receipts under an
// "Earlier" rule — the per-parish card history that answers "did I
// already handle Bob and Jane?" a week later. Receipts persist in
// Supabase and re-render here on reload; collapsing one is a view
// toggle (ActionCard owns it), never a delete.
export default function ActionCardPanel({
  cards,
  cardError,
  preparing,
  focusCardId,
  onApply,
  onRetry,
  onDismiss,
}: Props) {
  const pending = cards.filter((c) => c.status === "pending");
  const receipts = cards.filter((c) => c.status === "applied");
  const focusRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focusCardId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusCardId]);

  return (
    <aside className="card-panel" aria-label="Action cards">
      {cardError ? (
        <p className="card-panel__error">
          Couldn't prepare an action card for that — try rephrasing with the
          volunteer's full name and the dates.
        </p>
      ) : null}

      {preparing ? <PlaceholderCard /> : null}

      {pending.map((card) => (
        <div key={card.id} ref={card.id === focusCardId ? focusRef : undefined}>
          <ActionCard
            card={card}
            highlighted={card.id === focusCardId}
            onApply={(lineIds) => onApply(card.id, lineIds)}
            onRetry={(lineId) => onRetry(card.id, lineId)}
            onDismiss={() => onDismiss(card.id)}
          />
        </div>
      ))}

      {receipts.length > 0 ? (
        <>
          <h3 className="card-panel__earlier">Earlier</h3>
          {receipts.map((card) => (
            <div key={card.id} ref={card.id === focusCardId ? focusRef : undefined}>
              <ActionCard
                card={card}
                onApply={(lineIds) => onApply(card.id, lineIds)}
                onRetry={(lineId) => onRetry(card.id, lineId)}
                onDismiss={() => onDismiss(card.id)}
              />
            </div>
          ))}
        </>
      ) : null}
    </aside>
  );
}

// Faint skeleton shown while the worker validates + persists the card.
function PlaceholderCard() {
  return (
    <article className="action-card action-card--placeholder" aria-hidden="true">
      <div className="action-card__placeholder-head">
        <span className="skeleton skeleton--title" />
        <span className="action-card__placeholder-label">Preparing card…</span>
      </div>
      <span className="skeleton skeleton--line" />
      <span className="skeleton skeleton--line" />
      <span className="skeleton skeleton--line skeleton--short" />
    </article>
  );
}
